import React from 'react'
import { useI18n } from '../../i18n'
import styles from '../../styles/components/assets/AssetsRiskCompliance.module.scss'
import type { GrcSummaryRow } from '../../services/informationSystemComplianceApi'
import { SystemIdentityCell, SectionCell, MetricDisplay } from './riskComplianceRowParts'

interface Props {
  row: GrcSummaryRow
  selected?: boolean
  onSelect: () => void
  onOpenPassport?: () => void
  onOpenGraph?: () => void
}

export default function RiskComplianceSummaryRow({
  row,
  selected,
  onSelect,
  onOpenPassport,
  onOpenGraph,
}: Props) {
  const { t } = useI18n()
  const unknown = t('common.unknown', '—')
  const notAvailable = t('assets.riskCompliance.notAvailable', 'N/A')

  return (
    <article
      className={`${styles.summaryRow} ${selected ? styles.summaryRowSelected : ''}`.trim()}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      }}
      role="button"
      tabIndex={0}
      aria-selected={selected}
    >
      <div className={styles.rowInner}>
        <div className={styles.rowGrid}>
          <SystemIdentityCell row={row} />
          <SectionCell label={t('assets.grc.complianceScope', 'Standards')}>
            <MetricDisplay value={row.applicableStandardsCount} unknownLabel={unknown} />
          </SectionCell>
          <SectionCell label={t('assets.grc.controlCoverage', 'Coverage')}>
            <MetricDisplay
              value={row.controlCoveragePercent != null ? `${row.controlCoveragePercent}%` : null}
              unknownLabel={notAvailable}
            />
          </SectionCell>
          <SectionCell label={t('assets.grc.controlsMapped', 'Controls')}>
            <MetricDisplay
              value={row.controlsTotal}
              sub={`${row.controlsImplemented} ${t('assets.grc.implementation.IMPLEMENTED', 'impl.')}`}
              unknownLabel={unknown}
            />
          </SectionCell>
          <SectionCell label={t('assets.grc.evidence', 'Evidence')}>
            <MetricDisplay value={row.evidenceLinkedCount} unknownLabel={unknown} />
          </SectionCell>
          <SectionCell label={t('assets.grc.remediation', 'Remediation')}>
            <MetricDisplay value={row.remediationOpenCount} sub={t('assets.grc.remediationOpen', 'open')} unknownLabel={unknown} />
          </SectionCell>
          <SectionCell label={t('assets.riskCompliance.riskScore', 'Scores')}>
            <MetricDisplay value={null} sub={t('assets.riskCompliance.complianceScore', 'Compliance N/A')} unknownLabel={notAvailable} />
          </SectionCell>
          <div className={styles.rowActions} onClick={(e) => e.stopPropagation()}>
            {onOpenPassport ? (
              <button type="button" className={styles.rowActionBtn} onClick={onOpenPassport}>
                {t('assets.grc.openPassport', 'Passport')}
              </button>
            ) : null}
            {onOpenGraph ? (
              <button type="button" className={styles.rowActionBtn} onClick={onOpenGraph}>
                {t('assets.graph.openGraph', 'Graph')}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  )
}
